import {fontFetchers} from '@contentlab/api/fonts';
import {
  FontLoadingStatus,
  FontLoadingResult,
} from '@contentlab/api/fonts/types';
import {fontsStoreActions} from '@contentlab/stores/fontStore';
import {FontVariants} from '@contentlab/types/captions';
import {FontDownloader} from './FontDownloader';

/**
 * Font Manager - high-level interface for font management
 * Provides fonts list and handles loading coordination
 */
class FontManager {
  /**
   * Initialize the font manager by fetching all fonts metadata and preloading them
   */
  static async initialize(): Promise<void> {
    if (FontManager.isInitialized()) {
      return;
    }

    const fontResponses = await fontFetchers.getAllFonts();
    fontsStoreActions.setFontsMetadata(fontResponses);

    FontManager.preloadAllFonts().catch((error: any) => {
      console.warn(
        'FontManager: Background preloading had some failures, but fonts will load on demand:',
        error
      );
    });
  }

  static isInitialized(): boolean {
    return fontsStoreActions.getAllFonts().length > 0;
  }

  /**
   * Load a specific font variant and ensure it's ready to use
   * Downloads from backend if metadata not available
   */
  static async loadFont(
    fontId: string,
    variant: FontVariants
  ): Promise<FontLoadingResult> {
    if (fontsStoreActions.isFontLoaded(fontId, variant)) {
      return {status: FontLoadingStatus.LOADED};
    }
    if (fontsStoreActions.isFontLoading(fontId, variant)) {
      return {status: FontLoadingStatus.LOADING};
    }

    fontsStoreActions.setFontLoading(fontId, variant);

    try {
      let metadata = fontsStoreActions.getFontMetadata(fontId);
      if (!metadata) {
        const fontResponse = await fontFetchers.getFontById(fontId);
        fontsStoreActions.setFontMetadata(fontResponse.id, fontResponse);
        metadata = fontResponse;
      }

      const result = await FontDownloader.loadFont(fontId, variant);

      if (result.status === FontLoadingStatus.LOADED) {
        fontsStoreActions.setFontLoaded(fontId, variant);
      } else if (result.status === FontLoadingStatus.ERROR) {
        fontsStoreActions.setFontError(
          fontId,
          variant,
          result.error || 'Unknown error'
        );
      }

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load font';
      fontsStoreActions.setFontError(fontId, variant, errorMessage);
      return {
        status: FontLoadingStatus.ERROR,
        error: errorMessage,
      };
    }
  }

  /**
   * Preload fonts used in caption style presets
   */
  static async preloadFontsFromStyles(styles: any[]): Promise<void> {
    const fontMap = new Map<string, Set<FontVariants>>();

    // Extract font-variant pairs from styles
    styles.forEach((style) => {
      const fontSettings = style.captionsStyle?.typography?.fontSettings;
      const selectedFont = fontSettings?.selectedFont;

      if (selectedFont?.fontId && selectedFont?.variant) {
        const {fontId, variant} = selectedFont;

        if (!fontMap.has(fontId)) {
          fontMap.set(fontId, new Set());
        }

        fontMap.get(fontId)!.add(variant);
      }
    });

    // Create load promises for each font-variant pair
    const loadPromises: Promise<FontLoadingResult>[] = [];
    fontMap.forEach((variants, fontId) => {
      variants.forEach((variant) => {
        loadPromises.push(FontManager.loadFont(fontId, variant));
      });
    });

    const results = await Promise.allSettled(loadPromises);

    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      console.warn(`FontManager: Failed to preload ${failed} font variants`);
    }
  }

  /**
   * Aggressively preload ALL available fonts and variants
   */
  static async preloadAllFonts(): Promise<void> {
    const loadPromises: Promise<FontLoadingResult>[] = [];

    fontsStoreActions.getAllFontsMetadata().forEach((font) => {
      const availableVariants = Object.keys(
        font.downloadUrls
      ) as FontVariants[];
      availableVariants.forEach((variant) => {
        loadPromises.push(FontManager.loadFont(font.id, variant));
      });
    });

    const results = await Promise.allSettled(loadPromises);

    const failed = results.filter((r) => r.status === 'rejected').length;

    if (failed > 0) {
      console.warn(
        `FontManager: ${failed} font variants failed to preload (will load on demand)`
      );
    }
  }

  /**
   * Reset the font manager (useful for testing)
   */
  static reset(): void {
    FontDownloader.clearCache();
  }
}

export {FontManager};
export const fontManager = new FontManager();
export default fontManager;
